import { useEffect, useRef, useState } from 'react';
import { Field, Form, Formik } from 'formik';
import { v4 as uuidv4 } from 'uuid';

import {
  Box,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Flex,
  Checkbox,
  Text,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Select,
  Spacer,
  Divider,
  Button,
  Heading,
  useColorModeValue,
  Tag,
  TagLeftIcon,
  TagLabel,
} from '@chakra-ui/react';

import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';

import useToastNotif from 'hooks/useToastNotif';
import { TOAST_SUCCESS } from 'utils/constants/toast';

import { ReducerStatus } from 'utils/constants';
import { arrayOfString } from 'utils/jsonParser';

import SubQuestForm from 'components/subQuest/SubQuestForm';
import FormikFormControl from 'components/common/forms/FormikFormControl';
import ConfirmationActionModal from 'components/common/modals/ConfirmationActionModal';
import StayOnPageModal from 'components/common/modals/StayOnPageModal';
import QuestionAdminList from 'components/question/admin/QuestionAdminList';
import Breadcrumbs from 'components/common/Breadcrumbs';

import { subquestActions } from 'store/modules/subquest/subquest-slice';
import {
  updateQuizSubQuest,
  updateQuizSubQuestQuestions,
  updateSubQuestAction,
  getSubQuestById,
} from 'store/modules/subquest/subquest-action';
import { fetchQuestByIdAction } from 'store/modules/quest/quest-actions';
import {
  convertSecondsToTimeFormat,
  convertTimeBeforeNextAttemptToSeconds,
  quizSettingsValidation,
} from 'utils/quizHelper';

import LoadingSpinner from 'components/common/loaders/LoadingSpinner';

import { UPDATE_MODE } from 'utils/constants/operationMode';
import exportQuestionsAsCsv from 'utils/questions/exportQuestionsCsv';
import importQuestionsFromCsv from 'utils/questions/importQuestionsCsv';
import { QuizConfirmationDescription } from 'components/subQuest/quizzes/QuizConfirmationDescription';
import { RiDraftLine } from 'react-icons/ri';

function UpdateQuizPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { subQuestId } = useParams();
  const formikForm = useRef();

  const { generateToastNotif } = useToastNotif();

  const footerBg = useColorModeValue('white', 'gray.900');

  const topRef = useRef(null);

  const [triggerSubQuestForm, setTriggerSubQuestForm] = useState(0);
  const [isSubQuestFormValid, setIsSubQuestFormValid] = useState(false);
  const [isSaveSettings, setIsSaveSettings] = useState(false);
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [showClearConfirmation, setShowClearConfirmation] = useState(false);
  const [showReturnConfirmation, setShowReturnConfirmation] = useState(false);
  const [showImportConfirmation, setShowImportConfirmation] = useState(false);
  const [importData, setImportData] = useState(null);
  const [importErrors, setImportErrors] = useState([]);
  const [importStatus, setImportStatus] = useState(ReducerStatus.IDLE);
  const [saveConfirmationStatus, setSaveConfirmationStatus] = useState(
    ReducerStatus.IDLE
  );
  const [clearConfirmationStatus, setClearConfirmationStatus] = useState(
    ReducerStatus.IDLE
  );

  const [subQuestFormInputs, setSubQuestFormInputs] = useState({
    name: '',
    objective: '',
    code: '',
    isPublished: '',
    tags: [],
    groups: [],
  });

  const [formInputs, setFormInputs] = useState({
    scoreType: 'Actual Score',
    hasTimeLimit: true,
    timeLimit: 5,
    passingScore: 1,
    isShuffled: true,
    showAnswers: true,
    showResult: true,
    unlimitedAttempts: true,
    numberOfAttempts: 3,
    hasIntervalBeforeNextAttempt: false,
    intervalBeforeNextAttempt: 60,
    fetchFromQuestionBank: false,
    totalMark: 0,
  });

  const [timeBeforeNextAttempt, setTimeBeforeNextAttempt] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
  });
  const [questions, setQuestions] = useState([]);
  const [tabIndex, setTabIndex] = useState(0);

  // Collapse/Expand all button state
  const [forceCollapseAll, setForceCollapseAll] = useState(false);
  const [forceExpandAll, setForceExpandAll] = useState(false);
  const [allQuestionsCollapsed, setAllQuestionsCollapsed] = useState(false);

  // Error states for ConfirmationActionModal for local storage settings
  const [saveSettingsError, setSaveSettingsError] = useState(null);
  const [clearSettingsError, setClearSettingsError] = useState(null);

  // Draft states
  const [tabDraftsLoaded, setTabDraftsLoaded] = useState({
    general: false,
    settings: false,
    questions: false,
  });
  const [showDraftTag, setShowDraftTag] = useState(false);
  const [tabsCleared, setTabsCleared] = useState({
    general: false,
    settings: false,
    questions: false,
  });

  // Per-tab update confirmation states
  const [showGeneralConfirmation, setShowGeneralConfirmation] = useState(false);
  const [pendingGeneralValues, setPendingGeneralValues] = useState(null);
  const [showSettingsConfirmation, setShowSettingsConfirmation] =
    useState(false);
  const [pendingQuizValues, setPendingQuizValues] = useState(null);
  const [showQuestionsConfirmation, setShowQuestionsConfirmation] =
    useState(false);

  const courseId = searchParams.get('courseId');
  const questId = searchParams.get('questId');

  // Redux selectors for update operations
  const subQuest = useSelector((state) => state.subquest.subQuest);
  const quest = useSelector((state) => state.subquest.quest);
  const updateQuiz = useSelector((state) => state.subquest.updateQuiz);
  const updateQuizQuestions = useSelector(
    (state) => state.subquest.updateQuizQuestions
  );
  const updateSubQuest = useSelector((state) => state.subquest.updateSubQuest);
  const getSubQuestStatus = useSelector(
    (state) => state.subquest.getSubQuest.status
  );

  useEffect(() => {
    setTimeout(() => {
      topRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }, []);

  /**
   * Helper function to generate storage keys for each tab
   * @param {number} tab - Tab index (0=General, 1=Settings, 2=Questions)
   * @param {string} subQuestId - The subquest ID
   * @returns {string} Storage key for the specified tab
   */
  const getStorageKey = (tab) => {
    const tabNames = ['general', 'settings', 'questions'];
    return subQuestId
      ? `updateQuizDraft_${tabNames[tab]}_${subQuestId}`
      : `updateQuizDraft_${tabNames[tab]}`;
  };

  /**
   * Load drafts from localStorage on mount
   * Loads each tab's draft separately and tracks which tabs have drafts
   */
  useEffect(() => {
    if (subQuestId) {
      try {
        const draftsStatus = {
          general: false,
          settings: false,
          questions: false,
        };

        // Load General tab draft
        const generalDraft = localStorage.getItem(getStorageKey(0, subQuestId));
        if (generalDraft) {
          const draft = JSON.parse(generalDraft);
          setSubQuestFormInputs(draft.subQuestFormInputs || subQuestFormInputs);
          draftsStatus.general = true;
        }

        // Load Settings tab draft
        const settingsDraft = localStorage.getItem(
          getStorageKey(1, subQuestId)
        );
        if (settingsDraft) {
          const draft = JSON.parse(settingsDraft);
          setFormInputs(draft.formInputs || formInputs);
          if (draft.timeBeforeNextAttempt) {
            setTimeBeforeNextAttempt(draft.timeBeforeNextAttempt);
          }
          draftsStatus.settings = true;
        }

        // Load Questions tab draft
        const questionsDraft = localStorage.getItem(
          getStorageKey(2, subQuestId)
        );
        if (questionsDraft) {
          const draft = JSON.parse(questionsDraft);
          setQuestions(draft.questions || []);
          draftsStatus.questions = true;
        }

        setTabDraftsLoaded(draftsStatus);
      } catch (err) {
        // Silently handle localStorage errors
        setTabDraftsLoaded({
          general: false,
          settings: false,
          questions: false,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subQuestId]);

  /**
   * Fetch existing subquest data from API
   * Always fetch regardless of draft availability to ensure fresh data for tabs without drafts
   */
  useEffect(() => {
    if (subQuestId) {
      dispatch(getSubQuestById({ questId, subQuestId }));
    }
  }, [subQuestId, questId, dispatch]);

  // Fetch quest data if not already loaded
  useEffect(() => {
    if (questId && !quest) {
      dispatch(fetchQuestByIdAction(questId))
        .unwrap()
        .then((questData) => {
          dispatch(subquestActions.setQuest(questData));
        });
    }
  }, [dispatch, questId, quest]);

  /**
   * Initialize form data when subquest is loaded from API
   * Only populate tabs that don't have drafts loaded
   */
  useEffect(() => {
    if (subQuest && subQuest.subQuestQuiz) {
      const { name, objective, code, isPublished, tags, ownerGroups } =
        subQuest;

      document.title = `Skill Quest | Edit Quiz - ${name}`;

      const {
        scoreType,
        hasTimeLimit,
        timeLimit,
        passingScore,
        isShuffled,
        showAnswers,
        showResult,
        unlimitedAttempts,
        numberOfAttempts,
        hasIntervalBeforeNextAttempt,
        intervalBeforeNextAttempt,
        fetchFromQuestionBank,
        totalMark,
      } = subQuest.subQuestQuiz;

      // Only populate General tab data if no draft exists for it
      if (!tabDraftsLoaded.general || tabsCleared.general === true) {
        setSubQuestFormInputs({
          name,
          objective,
          code,
          isPublished: isPublished.toString(),
          tags: arrayOfString(tags),
          groups: arrayOfString(ownerGroups),
        });
        setTabsCleared((prev) => ({ ...prev, general: false }));
      }

      // Only populate Settings tab data if no draft exists for it
      if (!tabDraftsLoaded.settings || tabsCleared.settings === true) {
        setTimeBeforeNextAttempt(
          convertSecondsToTimeFormat(intervalBeforeNextAttempt)
        );

        setFormInputs({
          scoreType,
          hasTimeLimit,
          timeLimit,
          passingScore,
          isShuffled,
          showAnswers,
          showResult,
          unlimitedAttempts,
          numberOfAttempts,
          hasIntervalBeforeNextAttempt,
          intervalBeforeNextAttempt,
          fetchFromQuestionBank,
          totalMark,
        });
        setTabsCleared((prev) => ({ ...prev, settings: false }));
      }

      // Only populate Questions tab data if no draft exists for it
      if (!tabDraftsLoaded.questions || tabsCleared.questions === true) {
        const updatedQuestions =
          subQuest.subQuestQuiz.questions
            ?.map((question) => ({
              id: question.id,
              tempId: uuidv4(),
              questionHtml: question.questionHtml,
              questionTypeId: question.questionType.id,
              multipleChoiceChoices: JSON.parse(
                question.multipleChoiceChoicesJson
              ),
              multipleChoiceAnswers: JSON.parse(
                question.multipleChoiceAnswersJson
              ),
              trueOrFalseAnswer: question.trueOrFalseAnswer,
              explanation: question.explanation,
              mark: question.mark,
              allowPartialPoints: question.allowPartialPoints,
              errors: [],
              autoCheckInstruction: question.autoCheckInstruction,
              isAllowedAutoCheck: question.isAllowedAutoCheck,
              position: Number(question.position ?? question.Position ?? 0),
            }))
            .sort(
              (a, b) => (Number(a.position) || 0) - (Number(b.position) || 0)
            ) || [];

        setQuestions(updatedQuestions);
        setTabsCleared((prev) => ({ ...prev, questions: false }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    subQuest,
    tabsCleared.general,
    tabsCleared.settings,
    tabsCleared.questions,
  ]);

  // Gets the current tab name based on tabIndex
  const getCurrentTabName = () => {
    switch (tabIndex) {
      case 0:
        return 'General';
      case 1:
        return 'Settings';
      case 2:
        return 'Questions';
      default:
        return '';
    }
  };

  const handleAddQuestion = () => {
    const newQuestion = {
      id: null,
      tempId: uuidv4(),
      questionHtml: '',
      questionTypeId: 1,
      multipleChoiceChoices: [
        { option: 'A', value: 'A' },
        { option: 'B', value: 'B' },
      ],
      multipleChoiceAnswers: [],
      trueOrFalseAnswer: true,
      explanation: '',
      mark: 1,
      allowPartialPoints: false,
      errors: ['Question cannot be blank. '],
      autoCheckInstruction: '',
      isAllowedAutoCheck: true,
      position: questions.length,
    };
    const newQuestions = [...questions, newQuestion];
    setQuestions(newQuestions);
  };

  const handleTabsChange = (index) => {
    if (index < 0 || index > 2) return;
    setTabIndex(index);
  };

  const handleUpdateQuestion = (index, updatedQuestion) => {
    const updatedQuestions = [...questions];
    updatedQuestions[index] = updatedQuestion;
    setQuestions(updatedQuestions);
  };

  const handleQuestionTypeChange = (index, question, newQuestionTypeId) => {
    const updatedQuestion = {
      ...question,
      questionTypeId: parseInt(newQuestionTypeId, 10),
    };
    handleUpdateQuestion(index, updatedQuestion);
  };

  const handleRemoveQuestion = (index) => {
    setQuestions(questions.filter((_, i) => i !== index));
  };

  const handleQuestionsReorder = (reorderedQuestions) => {
    setQuestions(reorderedQuestions);
  };

  const handleCollapseAll = () => {
    setForceCollapseAll(true);

    // Reset the trigger after a brief moment to allow for future collapses
    setTimeout(() => {
      setForceCollapseAll(false);
    }, 100);
  };

  const handleExpandAll = () => {
    setForceExpandAll(true);

    // Reset the trigger after a brief moment to allow for future expands
    setTimeout(() => {
      setForceExpandAll(false);
    }, 100);
  };

  // Handles toggling between collapse and expand all functionality
  const handleToggleCollapseExpand = () => {
    if (allQuestionsCollapsed) {
      handleExpandAll();
      setAllQuestionsCollapsed(false);
    } else {
      handleCollapseAll();
      setAllQuestionsCollapsed(true);
    }
  };

  // Update draft tag visibility when tab changes or draft status changes
  useEffect(() => {
    if (
      (tabDraftsLoaded.general && tabIndex === 0) ||
      (tabDraftsLoaded.settings && tabIndex === 1) ||
      (tabDraftsLoaded.questions && tabIndex === 2)
    ) {
      setShowDraftTag(true);
    } else {
      setShowDraftTag(false);
    }
  }, [tabIndex, tabDraftsLoaded]);

  // Routes to appropriate tab-specific handler with confirmation modal
  const handleTabUpdate = () => {
    switch (tabIndex) {
      case 0: // General tab
        setTriggerSubQuestForm((trigger) => trigger + 1);
        break;
      case 1: // Settings tab
        // Validate form first
        formikForm?.current?.validateForm().then((errors) => {
          if (Object.keys(errors).length === 0 && formikForm.current) {
            const { values } = formikForm.current;
            setPendingQuizValues(values);
            setShowSettingsConfirmation(true);
          }
        });
        break;
      case 2: // Questions tab
        setShowQuestionsConfirmation(true);
        break;
      default:
        break;
    }
  };

  // Sets pending values and shows confirmation modal
  const handleSubQuestFormSubmit = (values) => {
    const generalValues = {
      ...values,
      ownerGroups: values.groups,
    };

    setPendingGeneralValues(generalValues);
    setShowGeneralConfirmation(true);
  };

  /**
   * Handles confirmation for General tab update
   * Dispatches updateSubQuestAction with pending values
   */
  const handleConfirmGeneralUpdate = () => {
    if (!pendingGeneralValues && !isSubQuestFormValid) return;
    dispatch(
      updateSubQuestAction({
        id: subQuest?.id,
        data: pendingGeneralValues,
      })
    );
  };

  /**
   * Handles confirmation for Settings tab update
   * Dispatches updateQuizSubQuest with pending quiz values
   */
  const handleConfirmSettingsUpdate = async () => {
    if (!pendingQuizValues) return;

    let interval = pendingQuizValues.intervalBeforeNextAttempt;
    let time = pendingQuizValues.timeLimit;

    if (pendingQuizValues.hasIntervalBeforeNextAttempt) {
      interval = convertTimeBeforeNextAttemptToSeconds(timeBeforeNextAttempt);
    }
    if (pendingQuizValues.hasTimeLimit === false) {
      time = 1;
    }

    const mergedObject = {
      ...pendingQuizValues,
      timeLimit: time,
      intervalBeforeNextAttempt: interval,
      totalMark: questions.reduce((sum, question) => sum + question.mark, 0),
    };

    dispatch(
      updateQuizSubQuest({
        questId: quest?.id || questId,
        subQuestId: subQuest?.id,
        quizSubQuest: mergedObject,
      })
    );
  };

  /**
   * Handles confirmation for Questions tab update
   * Dispatches updateQuizSubQuestQuestions with current questions
   */
  const handleConfirmQuestionsUpdate = async () => {
    if (questions.length === 0) return;

    const totalErrorCount = questions.reduce((acc, entry) => {
      return acc + entry.errors.length;
    }, 0);

    if (totalErrorCount !== 0) return;

    const createdSubQuestQuizId = subQuest?.subQuestQuiz?.id;
    const updatedQuestions = questions.map((question, index) => ({
      ...question,
      SubQuestQuizId: createdSubQuestQuizId,
      position: index,
    }));

    dispatch(
      updateQuizSubQuestQuestions({
        subQuestId: subQuest?.id,
        questions: updatedQuestions,
      })
    );
  };

  /**
   * Handles the result of General tab update confirmation
   * Clears General tab draft on successful update
   */
  const handleGeneralConfirmationResult = () => {
    // Clear General tab draft on successful update
    if (updateSubQuest.status === ReducerStatus.SUCCEEDED) {
      try {
        // Update draft tracking state
        localStorage.removeItem(getStorageKey(0, subQuestId));
        setTabDraftsLoaded((prev) => ({ ...prev, general: false }));
      } catch (err) {
        // Silently handle localStorage errors
      }
    }

    setShowGeneralConfirmation(false);
    setPendingGeneralValues(null);
    dispatch(subquestActions.resetUpdateSubQuest());
    setShowReturnConfirmation(true);
  };

  /**
   * Handles the result of Settings tab update confirmation
   * Clears Settings tab draft on successful update
   */
  const handleQuizConfirmationResult = () => {
    if (updateQuiz.status === ReducerStatus.SUCCEEDED) {
      try {
        localStorage.removeItem(getStorageKey(1, subQuestId));
        // Update draft tracking state
        setTabDraftsLoaded((prev) => ({ ...prev, settings: false }));
      } catch (err) {
        // Silently handle localStorage errors
      }
    }

    setShowSettingsConfirmation(false);
    setPendingQuizValues(null);
    dispatch(subquestActions.resetQuizUpdate());
    setShowReturnConfirmation(true);
  };

  /**
   * Handles the result of Questions tab update confirmation
   * Clears Questions tab draft on successful update
   */
  const handleQuestionConfirmationResult = () => {
    // Clear Questions tab draft on successful update
    if (updateQuizQuestions.status === ReducerStatus.SUCCEEDED) {
      try {
        localStorage.removeItem(getStorageKey(2, subQuestId));
        // Update draft tracking state
        setTabDraftsLoaded((prev) => ({ ...prev, questions: false }));
      } catch (err) {
        // Silently handle localStorage errors
      }
    }

    setShowQuestionsConfirmation(false);
    dispatch(subquestActions.resetQuizUpdate());
    setShowReturnConfirmation(true);
  };

  /**
   * Handle localStorage save when isSaveSettings is true
   * Saves only the current tab's data to its specific storage key
   */
  useEffect(() => {
    if (isSaveSettings) {
      try {
        const storageKey = getStorageKey(tabIndex, subQuestId);
        const currentTabName = getCurrentTabName().toLowerCase();
        let payload;

        // Save only the relevant data for the current tab
        switch (tabIndex) {
          case 0: // General tab
            payload = { subQuestFormInputs };
            break;
          case 1: // Settings tab
            payload = { formInputs, timeBeforeNextAttempt };
            break;
          case 2: // Questions tab
            payload = { questions };
            break;
          default:
            payload = {};
        }

        localStorage.setItem(storageKey, JSON.stringify(payload));
        setTabDraftsLoaded((prev) => ({
          ...prev,
          [currentTabName]: true,
        }));
      } catch (err) {
        setSaveSettingsError(err);
      }

      // Reset the flag only after successful save
      setIsSaveSettings(false);
    }
    // ignore getStorageKey dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isSaveSettings,
    formInputs,
    subQuestFormInputs,
    questions,
    tabIndex,
    timeBeforeNextAttempt,
    subQuestId,
  ]);

  // Reset Redux state and navigate back to admin course page
  const handleConfirmReturn = () => {
    setShowReturnConfirmation(false);

    dispatch(subquestActions.resetSubQuestState());

    navigate(`/admin/courses/${courseId}`);
  };

  // Cancel return and stay on the page
  const handleCancelReturn = () => {
    setShowReturnConfirmation(false);
  };

  // Save draft to localStorage
  const handleSaveDraft = () => {
    setShowSaveConfirmation(true);
  };

  const handleConfirmSave = () => {
    setSaveConfirmationStatus(ReducerStatus.LOADING);

    // Set flag to indicate this is a save operation, not a submit
    setIsSaveSettings(true);
    // Trigger SubQuest form submission to capture current data
    setTriggerSubQuestForm((trigger) => trigger + 1);

    // Simulate async operation for consistency
    setTimeout(() => {
      setSaveConfirmationStatus(ReducerStatus.SUCCEEDED);
    }, 500);
  };

  const handleCancelSaveConfirmation = () => {
    setShowSaveConfirmation(false);
  };

  const handleSaveConfirmationResult = () => {
    setShowSaveConfirmation(false);
    setSaveConfirmationStatus(ReducerStatus.IDLE);
    generateToastNotif(
      TOAST_SUCCESS,
      'Draft Saved',
      "Quiz draft saved successfully to your browser's local storage.",
      true
    );
  };

  const handleClearDraft = () => {
    setShowClearConfirmation(true);
  };

  /**
   * Clears all draft data from localStorage for the current tab
   */
  const handleConfirmClear = () => {
    setClearConfirmationStatus(ReducerStatus.LOADING);

    setTimeout(() => {
      try {
        switch (tabIndex) {
          case 0:
            localStorage.removeItem(getStorageKey(0, subQuestId)); // General
            setTabDraftsLoaded((prev) => ({ ...prev, general: false }));
            setTabsCleared((prev) => ({ ...prev, general: true }));
            break;
          case 1:
            localStorage.removeItem(getStorageKey(1, subQuestId)); // Settings
            setTabDraftsLoaded((prev) => ({ ...prev, settings: false }));
            setTabsCleared((prev) => ({ ...prev, settings: true }));
            break;
          case 2:
            localStorage.removeItem(getStorageKey(2, subQuestId)); // Questions
            setTabDraftsLoaded((prev) => ({ ...prev, questions: false }));
            setTabsCleared((prev) => ({ ...prev, questions: true }));
            break;
          default:
            break;
        }
      } catch (err) {
        setClearSettingsError(err);
      }
      // Reset draft tracking states
      setClearConfirmationStatus(ReducerStatus.SUCCEEDED);
    }, 500);
  };

  const handleCancelClearConfirmation = () => {
    setShowClearConfirmation(false);
  };

  const handleClearConfirmationResult = () => {
    setShowClearConfirmation(false);
    setClearConfirmationStatus(ReducerStatus.IDLE);
    generateToastNotif(
      TOAST_SUCCESS,
      'Draft Cleared',
      "Quiz draft cleared successfully from your browser's local storage.",
      true
    );
  };

  /**
   * Handles exporting quiz questions to CSV format
   * Uses the custom exportCsv-Parse utility with specific column structure
   */
  const handleExportQuestions = () => {
    if (questions.length === 0) return;

    const exportFileName = `quiz-questions-${subQuest?.name || 'subQuest'}`;

    exportQuestionsAsCsv(questions, exportFileName);
  };

  /**
   * Handles importing quiz questions from CSV file
   * Processes the file and shows confirmation modal with preview
   */
  const handleImportQuestions = async (file) => {
    try {
      const result = await importQuestionsFromCsv(file);

      setImportData(result);
      setImportErrors(result.errors);
      setImportStatus(ReducerStatus.IDLE);
      setShowImportConfirmation(true);
    } catch (error) {
      setImportErrors([error.message]);
      setImportStatus(ReducerStatus.FAILED);
      setShowImportConfirmation(true);
    }
  };

  /**
   * Confirms the import and adds questions to existing list
   * Updates positions for all questions after import
   */
  const handleConfirmImport = () => {
    if (!importData || !importData.questions) return;

    setImportStatus(ReducerStatus.LOADING);

    const currentQuestionsCount = questions.length;

    // Update positions for imported questions to append after existing ones
    const questionsWithUpdatedPositions = importData.questions.map(
      (question, index) => ({
        ...question,
        position: currentQuestionsCount + index,
      })
    );

    // Add imported questions to existing questions
    const updatedQuestions = [...questions, ...questionsWithUpdatedPositions];
    setQuestions(updatedQuestions);

    setImportStatus(ReducerStatus.SUCCEEDED);

    generateToastNotif(
      TOAST_SUCCESS,
      'Questions Imported',
      `Successfully imported ${importData.questions.length} question(s) to your quiz.`,
      true
    );

    // Close import modal and reset state
    setShowImportConfirmation(false);
    setImportData(null);
    setImportErrors([]);
    setImportStatus(ReducerStatus.IDLE);
  };

  /**
   * Cancels the import process and resets import state
   */
  const handleCancelImport = () => {
    setShowImportConfirmation(false);
    setImportData(null);
    setImportErrors([]);
    setImportStatus(ReducerStatus.IDLE);
  };

  /**
   * Handles the result of import confirmation modal
   */
  const handleImportConfirmationResult = () => {
    setShowImportConfirmation(false);
    setImportData(null);
    setImportErrors([]);
    setImportStatus(ReducerStatus.IDLE);
  };

  return (
    <Box>
      {/* General Tab Update Confirmation Modal */}
      <ConfirmationActionModal
        isVisible={showGeneralConfirmation}
        onClose={() => {
          setShowGeneralConfirmation(false);
          setPendingGeneralValues(null);
        }}
        onConfirm={handleConfirmGeneralUpdate}
        onSubmitResult={handleGeneralConfirmationResult}
        status={updateSubQuest.status}
        successMessage="The sub-quest form has been updated successfully."
        error={updateSubQuest.error}
      >
        <QuizConfirmationDescription
          type="general"
          mode="update"
          data={{ subQuestFormInputs: pendingGeneralValues }}
        />
      </ConfirmationActionModal>

      {/* Settings Tab Update Confirmation Modal */}
      <ConfirmationActionModal
        isVisible={showSettingsConfirmation}
        onClose={() => {
          setShowSettingsConfirmation(false);
          setPendingQuizValues(null);
        }}
        onConfirm={handleConfirmSettingsUpdate}
        onSubmitResult={handleQuizConfirmationResult}
        status={updateQuiz.status}
        successMessage="The quiz settings have been updated successfully."
        error={updateQuiz.error}
      >
        <QuizConfirmationDescription
          type="quiz"
          mode="update"
          data={{ pendingFormValues: pendingQuizValues, questions }}
        />
      </ConfirmationActionModal>

      {/* Questions Tab Update Confirmation Modal */}
      <ConfirmationActionModal
        isVisible={showQuestionsConfirmation}
        onClose={() => setShowQuestionsConfirmation(false)}
        onConfirm={handleConfirmQuestionsUpdate}
        onSubmitResult={handleQuestionConfirmationResult}
        status={updateQuizQuestions.status}
        successMessage="The quiz questions have been updated successfully."
        error={updateQuizQuestions.error}
      >
        <QuizConfirmationDescription
          type="questions"
          mode="update"
          data={{ questions }}
        />
      </ConfirmationActionModal>

      {/* Save Draft Confirmation Modal */}
      <ConfirmationActionModal
        isVisible={showSaveConfirmation}
        onClose={handleCancelSaveConfirmation}
        onConfirm={handleConfirmSave}
        onSubmitResult={handleSaveConfirmationResult}
        status={saveConfirmationStatus}
        successMessage="Your progress has been saved successfully."
        error={saveSettingsError}
      >
        <QuizConfirmationDescription
          type="save"
          tabName={getCurrentTabName()}
          data={{
            questions,
            subQuestFormInputs,
            formInputs,
          }}
        />
      </ConfirmationActionModal>

      {/* Clear Draft Confirmation Modal */}
      <ConfirmationActionModal
        isVisible={showClearConfirmation}
        onClose={handleCancelClearConfirmation}
        onConfirm={handleConfirmClear}
        onSubmitResult={handleClearConfirmationResult}
        status={clearConfirmationStatus}
        successMessage="All settings and questions have been cleared."
        error={clearSettingsError}
      >
        <QuizConfirmationDescription
          type="clear"
          mode="update"
          tabName={getCurrentTabName()}
          data={{
            questions,
            subQuestFormInputs,
            formInputs,
          }}
        />
      </ConfirmationActionModal>

      {/* Return to Admin Course Page Confirmation Modal */}
      <StayOnPageModal
        isOpen={showReturnConfirmation}
        onClose={handleCancelReturn}
        onConfirm={handleConfirmReturn}
        title="Return to Admin Course Page?"
        message="Your quiz has been updated successfully. Would you like to return to the admin course page?"
      />

      {/* Import Questions Confirmation Modal */}
      <ConfirmationActionModal
        isVisible={showImportConfirmation}
        onClose={handleCancelImport}
        onConfirm={handleConfirmImport}
        onSubmitResult={handleImportConfirmationResult}
        status={importStatus}
        successMessage={`Successfully imported ${importData?.summary?.validQuestions || 0} questions.`}
        error={importStatus === ReducerStatus.FAILED ? importErrors[0] : null}
        confirmDisabled={
          importStatus === ReducerStatus.FAILED ||
          (importData?.summary?.validQuestions || 0) === 0
        }
      >
        <QuizConfirmationDescription
          type="import"
          data={{
            importStatus,
            importData,
            importErrors,
            questions,
          }}
        />
      </ConfirmationActionModal>

      <Flex minH="100vh" ml={{ base: 0, md: 20 }} ref={topRef}>
        <Box flex="1" ml={2} p="5">
          <Flex direction={{ base: 'column', lg: 'row' }}>
            <Box flex="1">
              {getSubQuestStatus === ReducerStatus.LOADING ? (
                <Box height="90vh">
                  <LoadingSpinner
                    thickness="10px"
                    size="xl"
                    message="Loading quiz sub-quest form..."
                  />
                </Box>
              ) : (
                <>
                  <Breadcrumbs
                    mb={4}
                    links={[
                      { name: 'Home', path: '/' },
                      { name: 'Courses', path: '/admin/courses' },
                      {
                        name: quest?.name,
                        path: `/admin/courses/${courseId}`,
                      },
                      {
                        name: 'Update Quiz',
                        path: `/admin/quizzes/update/${subQuestId}?courseId=${courseId}&questId=${questId}`,
                      },
                    ]}
                  />
                  <Flex>
                    <Heading as="h1" size="md" mb={4}>
                      Update Quiz Sub-Quest
                    </Heading>
                    <Spacer />
                    {showDraftTag && (
                      <Tag colorScheme="blue">
                        <TagLeftIcon as={RiDraftLine} />
                        <TagLabel>Draft Mode</TagLabel>
                      </Tag>
                    )}
                  </Flex>
                  <Tabs
                    variant="enclosed"
                    index={tabIndex}
                    onChange={handleTabsChange}
                  >
                    <TabList>
                      <Tab>General</Tab>
                      <Tab>Settings</Tab>
                      <Tab>Questions</Tab>
                    </TabList>
                    <TabPanels>
                      <TabPanel>
                        <SubQuestForm
                          questName={quest?.name}
                          groups={quest?.ownerGroups}
                          subQuestFormInputs={subQuestFormInputs}
                          triggerSubQuestForm={triggerSubQuestForm}
                          setSubQuestFormInputs={setSubQuestFormInputs}
                          setIsSubQuestFormValid={setIsSubQuestFormValid}
                          onSubmit={handleSubQuestFormSubmit}
                          isSaveSettings={isSaveSettings}
                          isUpdate
                        />
                      </TabPanel>
                      <TabPanel>
                        <Formik
                          enableReinitialize
                          innerRef={formikForm}
                          initialValues={formInputs}
                          validationSchema={quizSettingsValidation}
                        >
                          {() => (
                            <Box>
                              <Form>
                                <Flex>
                                  <Box flex="1" mr={5}>
                                    <Flex mt={5}>
                                      <Box>
                                        <Field name="isShuffled">
                                          {({ field, form }) => (
                                            <FormikFormControl
                                              errors={form.errors.isShuffled}
                                              touched={form.touched.isShuffled}
                                              form={form}
                                              label=""
                                              helperText=""
                                            >
                                              <Checkbox
                                                size="lg"
                                                mr={5}
                                                isChecked={field.value}
                                                onChange={(e) =>
                                                  setFormInputs({
                                                    ...formInputs,
                                                    isShuffled:
                                                      e.target.checked,
                                                  })
                                                }
                                              >
                                                Shuffle Questions
                                              </Checkbox>
                                              <Text fontSize="sm" color="gray">
                                                Randomize the order of questions
                                                each time the quiz is taken.
                                              </Text>
                                            </FormikFormControl>
                                          )}
                                        </Field>
                                      </Box>
                                    </Flex>
                                    <Flex align="center" mt={4}>
                                      <Box w="50%">
                                        <Field name="hasTimeLimit">
                                          {({ field, form }) => (
                                            <FormikFormControl
                                              form={form}
                                              label=""
                                              helperText=""
                                              errors={form.errors.hasTimeLimit}
                                              touched={
                                                form.touched.hasTimeLimit
                                              }
                                            >
                                              <Checkbox
                                                size="lg"
                                                mr={5}
                                                isChecked={field.value}
                                                onChange={(e) =>
                                                  setFormInputs({
                                                    ...formInputs,
                                                    hasTimeLimit:
                                                      e.target.checked,
                                                  })
                                                }
                                              >
                                                Time Limit
                                              </Checkbox>
                                            </FormikFormControl>
                                          )}
                                        </Field>
                                      </Box>
                                      <Box>
                                        {formInputs.hasTimeLimit === true && (
                                          <Field name="timeLimit">
                                            {({ field, form }) => (
                                              <FormikFormControl
                                                errors={form.errors.timeLimit}
                                                touched={form.touched.timeLimit}
                                                form={form}
                                                label=""
                                                helperText=""
                                              >
                                                <NumberInput
                                                  min={0}
                                                  max={100}
                                                  w="100px"
                                                  onChange={(value) =>
                                                    setFormInputs({
                                                      ...formInputs,
                                                      timeLimit:
                                                        value === ''
                                                          ? 0
                                                          : parseInt(value, 10),
                                                    })
                                                  }
                                                  value={field.value}
                                                >
                                                  <NumberInputField />
                                                  <NumberInputStepper>
                                                    <NumberIncrementStepper />
                                                    <NumberDecrementStepper />
                                                  </NumberInputStepper>
                                                </NumberInput>
                                              </FormikFormControl>
                                            )}
                                          </Field>
                                        )}
                                      </Box>
                                      {formInputs.hasTimeLimit === true && (
                                        <Box ml={2}>minutes</Box>
                                      )}
                                    </Flex>
                                    <Flex mt={-2}>
                                      <Text fontSize="sm" color="gray">
                                        Check this box if you want to impose a
                                        time limit on the quiz
                                      </Text>
                                    </Flex>
                                    <Flex align="center" mt={5}>
                                      <Box w="50%">
                                        <Field name="unlimitedAttempts">
                                          {({ field, form }) => (
                                            <FormikFormControl
                                              errors={
                                                form.errors.unlimitedAttempts
                                              }
                                              touched={
                                                form.touched.unlimitedAttempts
                                              }
                                              form={form}
                                              label=""
                                              helperText=""
                                            >
                                              <Checkbox
                                                size="lg"
                                                mr={5}
                                                isChecked={field.value}
                                                onChange={(e) =>
                                                  setFormInputs({
                                                    ...formInputs,
                                                    unlimitedAttempts:
                                                      e.target.checked,
                                                  })
                                                }
                                              >
                                                Unlimited Attempts
                                              </Checkbox>
                                            </FormikFormControl>
                                          )}
                                        </Field>
                                      </Box>
                                      <Box>
                                        {formInputs.unlimitedAttempts ===
                                          false && (
                                          <Field name="numberOfAttempts">
                                            {({ field, form }) => (
                                              <FormikFormControl
                                                errors={
                                                  form.errors.numberOfAttempts
                                                }
                                                touched={
                                                  form.touched.numberOfAttempts
                                                }
                                                form={form}
                                                label=""
                                                helperText=""
                                              >
                                                <NumberInput
                                                  min={0}
                                                  w="100px"
                                                  onChange={(value) =>
                                                    setFormInputs({
                                                      ...formInputs,
                                                      numberOfAttempts:
                                                        value === ''
                                                          ? 0
                                                          : parseInt(value, 10),
                                                    })
                                                  }
                                                  value={field.value}
                                                >
                                                  <NumberInputField
                                                    {...field}
                                                  />
                                                  <NumberInputStepper>
                                                    <NumberIncrementStepper />
                                                    <NumberDecrementStepper />
                                                  </NumberInputStepper>
                                                </NumberInput>
                                              </FormikFormControl>
                                            )}
                                          </Field>
                                        )}
                                      </Box>
                                      {formInputs.unlimitedAttempts ===
                                        false && <Box ml={2}>attempts</Box>}
                                    </Flex>
                                    <Flex>
                                      <Text fontSize="sm" color="gray">
                                        Allow participants to retake the quiz an
                                        unlimited number of times.
                                      </Text>
                                    </Flex>
                                    <Flex mt={5}>
                                      <Box>
                                        <Field name="hasIntervalBeforeNextAttempt">
                                          {({ field, form }) => (
                                            <FormikFormControl
                                              errors={
                                                form.errors
                                                  .hasIntervalBeforeNextAttempt
                                              }
                                              touched={
                                                form.touched
                                                  .hasIntervalBeforeNextAttempt
                                              }
                                              form={form}
                                              label=""
                                              helperText=""
                                            >
                                              <Checkbox
                                                size="lg"
                                                mr={5}
                                                isChecked={field.value}
                                                onChange={(e) =>
                                                  setFormInputs({
                                                    ...formInputs,
                                                    hasIntervalBeforeNextAttempt:
                                                      e.target.checked,
                                                  })
                                                }
                                              >
                                                Interval Before Next Attempt
                                              </Checkbox>
                                            </FormikFormControl>
                                          )}
                                        </Field>
                                      </Box>
                                      <Box>
                                        {formInputs.hasIntervalBeforeNextAttempt ===
                                          true && (
                                          <Flex align="center">
                                            <Box>
                                              <Flex>
                                                <Box w="70px" mr={2}>
                                                  <Text
                                                    fontWeight="medium"
                                                    fontSize="sm"
                                                  >
                                                    days
                                                  </Text>
                                                  <NumberInput
                                                    min={0}
                                                    size="md"
                                                    onChange={(value) =>
                                                      setTimeBeforeNextAttempt({
                                                        ...timeBeforeNextAttempt,
                                                        days: value,
                                                      })
                                                    }
                                                    value={
                                                      timeBeforeNextAttempt.days
                                                    }
                                                  >
                                                    <NumberInputField />
                                                    <NumberInputStepper>
                                                      <NumberIncrementStepper />
                                                      <NumberDecrementStepper />
                                                    </NumberInputStepper>
                                                  </NumberInput>
                                                </Box>
                                                <Box w="70px" mr={2}>
                                                  <Text
                                                    fontWeight="medium"
                                                    fontSize="sm"
                                                  >
                                                    hours
                                                  </Text>
                                                  <NumberInput
                                                    min={0}
                                                    max={23}
                                                    size="md"
                                                    onChange={(value) =>
                                                      setTimeBeforeNextAttempt({
                                                        ...timeBeforeNextAttempt,
                                                        hours: value,
                                                      })
                                                    }
                                                    value={
                                                      timeBeforeNextAttempt.hours
                                                    }
                                                  >
                                                    <NumberInputField />
                                                    <NumberInputStepper>
                                                      <NumberIncrementStepper />
                                                      <NumberDecrementStepper />
                                                    </NumberInputStepper>
                                                  </NumberInput>
                                                </Box>
                                                <Box w="70px" mr={2}>
                                                  <Text
                                                    fontWeight="medium"
                                                    fontSize="sm"
                                                  >
                                                    minutes
                                                  </Text>
                                                  <NumberInput
                                                    defaultValue={0}
                                                    min={0}
                                                    max={59}
                                                    size="md"
                                                    onChange={(value) =>
                                                      setTimeBeforeNextAttempt({
                                                        ...timeBeforeNextAttempt,
                                                        minutes: value,
                                                      })
                                                    }
                                                    value={
                                                      timeBeforeNextAttempt.minutes
                                                    }
                                                  >
                                                    <NumberInputField />
                                                    <NumberInputStepper>
                                                      <NumberIncrementStepper />
                                                      <NumberDecrementStepper />
                                                    </NumberInputStepper>
                                                  </NumberInput>
                                                </Box>
                                              </Flex>
                                            </Box>
                                          </Flex>
                                        )}
                                      </Box>
                                    </Flex>
                                    <Flex>
                                      <Text fontSize="sm" color="gray">
                                        Set a waiting period before participants
                                        can retake the quiz.
                                      </Text>
                                    </Flex>
                                  </Box>
                                  <Box flex="1">
                                    <Flex>
                                      <Box flex="1" mr={5}>
                                        <Field name="scoreType">
                                          {({ field, form }) => (
                                            <FormikFormControl
                                              label="Score Type"
                                              helperText="Compute scores as a percentage or as an actual score."
                                              errors={form.errors.scoreType}
                                              touched={form.touched.scoreType}
                                              form={form}
                                            >
                                              <Select
                                                onChange={(e) =>
                                                  setFormInputs({
                                                    ...formInputs,
                                                    scoreType: e.target.value,
                                                  })
                                                }
                                                value={field.value}
                                              >
                                                <option value="Actual Score">
                                                  Actual Score
                                                </option>
                                                <option value="Percentage">
                                                  Percentage
                                                </option>
                                              </Select>
                                            </FormikFormControl>
                                          )}
                                        </Field>
                                      </Box>
                                      <Box flex="1" mr={5}>
                                        <Field name="passingScore">
                                          {({ field, form }) => (
                                            <FormikFormControl
                                              label={
                                                formInputs.scoreType ===
                                                'Percentage'
                                                  ? 'Passing Score (%)'
                                                  : 'Passing Score'
                                              }
                                              helperText="Define the score needed to pass the quiz based on the Score Setting."
                                              errors={form.errors.passingScore}
                                              touched={
                                                form.touched.passingScore
                                              }
                                              form={form}
                                            >
                                              <NumberInput
                                                min={0}
                                                max={100}
                                                w="100px"
                                                onChange={(value) =>
                                                  setFormInputs({
                                                    ...formInputs,
                                                    passingScore:
                                                      value === '' ||
                                                      value === undefined
                                                        ? formInputs.passingScore
                                                        : parseInt(value, 10),
                                                  })
                                                }
                                                value={field.value}
                                              >
                                                <NumberInputField {...field} />
                                                <NumberInputStepper>
                                                  <NumberIncrementStepper />
                                                  <NumberDecrementStepper />
                                                </NumberInputStepper>
                                              </NumberInput>
                                            </FormikFormControl>
                                          )}
                                        </Field>
                                      </Box>
                                    </Flex>
                                    <Flex mt={5}>
                                      <Box w="50%">
                                        <Field name="showResult">
                                          {({ field, form }) => (
                                            <FormikFormControl
                                              errors={form.errors.showResult}
                                              touched={form.touched.showResult}
                                              form={form}
                                              label=""
                                              helperText=""
                                            >
                                              <Checkbox
                                                size="lg"
                                                mr={5}
                                                isChecked={field.value}
                                                onChange={(e) =>
                                                  setFormInputs({
                                                    ...formInputs,
                                                    showResult:
                                                      e.target.checked,
                                                  })
                                                }
                                              >
                                                Show Result
                                              </Checkbox>
                                            </FormikFormControl>
                                          )}
                                        </Field>
                                        <Text fontSize="sm" color="gray">
                                          Display scores right after the quiz
                                          for instant feedback.
                                        </Text>
                                      </Box>
                                      <Box w="50%">
                                        <Field name="showAnswers">
                                          {({ field, form }) => (
                                            <FormikFormControl
                                              errors={form.errors.showAnswers}
                                              touched={form.touched.showAnswers}
                                              form={form}
                                              label=""
                                              helperText=""
                                            >
                                              <Checkbox
                                                size="lg"
                                                mr={5}
                                                isChecked={field.value}
                                                onChange={(e) =>
                                                  setFormInputs({
                                                    ...formInputs,
                                                    showAnswers:
                                                      e.target.checked,
                                                  })
                                                }
                                              >
                                                Show Answers
                                              </Checkbox>
                                            </FormikFormControl>
                                          )}
                                        </Field>
                                        <Text fontSize="sm" color="gray">
                                          Allow participants to view correct
                                          answers after the quiz.
                                        </Text>
                                      </Box>
                                    </Flex>
                                  </Box>
                                </Flex>
                              </Form>
                            </Box>
                          )}
                        </Formik>
                      </TabPanel>
                      <TabPanel>
                        <QuestionAdminList
                          questions={questions}
                          formInputs={formInputs}
                          mode={UPDATE_MODE}
                          onAddQuestion={handleAddQuestion}
                          onUpdateQuestion={handleUpdateQuestion}
                          onRemoveQuestion={handleRemoveQuestion}
                          onQuestionTypeChange={handleQuestionTypeChange}
                          onToggleCollapseExpand={handleToggleCollapseExpand}
                          allQuestionsCollapsed={allQuestionsCollapsed}
                          forceCollapseAll={forceCollapseAll}
                          forceExpandAll={forceExpandAll}
                          onQuestionsReorder={handleQuestionsReorder}
                          onExportQuestions={handleExportQuestions}
                          onImportQuestions={handleImportQuestions}
                        />
                      </TabPanel>
                    </TabPanels>
                  </Tabs>
                </>
              )}

              <Divider mt={6} />

              {/* Footer actions similar to modal footer */}
              <Box
                mt={4}
                position="sticky"
                bottom={0}
                bg={footerBg}
                py={3}
                mr={20}
              >
                <Flex justify="flex-end">
                  <Button
                    variant="outline"
                    colorScheme="blue"
                    mr={4}
                    onClick={() => {
                      navigate(`/admin/courses/${courseId}`);
                    }}
                    isLoading={
                      updateSubQuest.status === ReducerStatus.LOADING ||
                      updateQuiz.status === ReducerStatus.LOADING ||
                      updateQuizQuestions.status === ReducerStatus.LOADING
                    }
                  >
                    Close
                  </Button>
                  <Button
                    type="button"
                    colorScheme="red"
                    isDisabled={tabIndex === 0}
                    isLoading={
                      updateSubQuest.status === ReducerStatus.LOADING ||
                      updateQuiz.status === ReducerStatus.LOADING ||
                      updateQuizQuestions.status === ReducerStatus.LOADING
                    }
                    px={10}
                    mr={4}
                    onClick={() => {
                      handleTabsChange(tabIndex - 1);
                    }}
                    variant="outline"
                  >
                    Back
                  </Button>
                  <Button
                    type="button"
                    colorScheme="red"
                    isDisabled={tabIndex === 2}
                    isLoading={
                      updateSubQuest.status === ReducerStatus.LOADING ||
                      updateQuiz.status === ReducerStatus.LOADING ||
                      updateQuizQuestions.status === ReducerStatus.LOADING
                    }
                    px={10}
                    onClick={() => {
                      handleTabsChange(tabIndex + 1);
                    }}
                    variant="outline"
                  >
                    Next
                  </Button>
                  <Spacer />
                  <Button
                    variant="outline"
                    colorScheme="blue"
                    mr={4}
                    isLoading={
                      updateSubQuest.status === ReducerStatus.LOADING ||
                      updateQuiz.status === ReducerStatus.LOADING ||
                      updateQuizQuestions.status === ReducerStatus.LOADING
                    }
                    onClick={handleClearDraft}
                  >
                    Clear Draft
                  </Button>
                  <Button
                    mr={4}
                    isLoading={
                      updateSubQuest.status === ReducerStatus.LOADING ||
                      updateQuiz.status === ReducerStatus.LOADING ||
                      updateQuizQuestions.status === ReducerStatus.LOADING
                    }
                    variant="outline"
                    colorScheme="red"
                    onClick={() => {
                      handleSaveDraft();
                    }}
                  >
                    Save Draft
                  </Button>
                  <Button
                    isLoading={
                      updateSubQuest.status === ReducerStatus.LOADING ||
                      updateQuiz.status === ReducerStatus.LOADING ||
                      updateQuizQuestions.status === ReducerStatus.LOADING
                    }
                    onClick={handleTabUpdate}
                    colorScheme="red"
                    px={10}
                  >
                    Update ({getCurrentTabName()})
                  </Button>
                </Flex>
              </Box>
            </Box>
          </Flex>
        </Box>
      </Flex>
    </Box>
  );
}

export default UpdateQuizPage;
